import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useSnackbar } from 'src/components/snackbar';
import { useLocales } from 'src/locales';
import {
  syncLogisticaProducers,
  syncLogisticaProducts,
  syncLogisticaClients,
  syncLogisticaStock,
} from 'src/api/admin';

type SyncType = 'products' | 'producers' | 'clients' | 'stock';

export default function LogisticaSyncControls() {
  const { t } = useLocales('logisticaSync');
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState({
    products: false,
    producers: false,
    clients: false,
    stock: false,
  });

  const syncActions: Record<SyncType, () => Promise<any>> = {
    producers: syncLogisticaProducers,
    products: syncLogisticaProducts,
    clients: syncLogisticaClients,
    stock: syncLogisticaStock,
  };

  const handleSync = async (type: SyncType) => {
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      await syncActions[type]();
      enqueueSnackbar(t('sync_success'), { variant: 'success' });
    } catch (error) {
      console.error('Sync failed:', error);
      enqueueSnackbar(error?.response?.data?.message || t('sync_error'), { variant: 'error' });
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const buttons = [
    { type: 'producers' as const, labelKey: 'sync_producers', loadingKey: 'syncing_producers' },
    { type: 'products' as const, labelKey: 'sync_products', loadingKey: 'syncing_products' },
    { type: 'clients' as const, labelKey: 'sync_clients', loadingKey: 'syncing_clients' },
    { type: 'stock' as const, labelKey: 'sync_stock', loadingKey: 'syncing_stock' },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('title')}
        </Typography>
        <Stack direction="row" spacing={2}>
          {buttons.map(({ type, labelKey, loadingKey }) => (
            <Button
              key={type}
              variant="contained"
              onClick={() => handleSync(type)}
              disabled={loading[type]}
            >
              {loading[type] ? t(loadingKey) : t(labelKey)}
            </Button>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
